/**
 * V2 rebuild against Figma frame a_start_pick_team_name (393×999)
 * and c_start_pick_team_name_error for the error state.
 * Functional contract preserved: deep-link guard, member polling,
 * PATCH on submit, error mapping, sessionStorage write, navigation.
 */
import { Box, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { ApiError } from '../../api/client';
import { getTeam, patchTeamName } from '../../api/teams';
import ClavisTextField from '../../components/ClavisTextField';
import OnboardingShellV2 from '../../components/OnboardingShellV2';
import ProgressIndicatorV2 from '../../components/ProgressIndicatorV2';
import heroFerris from '../../assets/onboarding/hero-team-ferris.svg';
import infoIcon from '../../assets/onboarding/info.svg';
import { useWizardState } from '../../wizard/useWizardState';
import { getStepInfo } from '../../wizard/wizardSteps';
import { pillButtonFocusRing } from '../../styles/onboardingButtons';

interface MemberEntry {
  id: string;
  display_name: string;
}

const POLL_INTERVAL_MS = 5_000;

const PickTeamNameStep: React.FC = () => {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const { code: rallyeCode } = useParams<{ code: string }>();
  const wizard = useWizardState();
  const tf = wizard.teamFlow;

  const [submitting, setSubmitting] = useState(false);
  const [members, setMembers] = useState<MemberEntry[]>([]);
  const pollTimerRef = useRef<number | null>(null);

  const step = getStepInfo('team-pick-name', 'with-code');
  const suggestions: string[] = t('pickName.suggestions', { returnObjects: true }) as string[];

  useEffect(() => {
    if (!tf.teamId && rallyeCode) {
      navigate(`/join/${rallyeCode}/team/invite`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!tf.teamId || !tf.runId || !wizard.organizationSlug) return;

    const fetchOnce = async () => {
      try {
        const team = await getTeam({
          orgSlug: wizard.organizationSlug!,
          runId: tf.runId!,
          teamId: tf.teamId!,
        });
        if (team.members) {
          setMembers(
            team.members.map((m) => ({ id: m.id, display_name: m.display_name })),
          );
        }
      } catch {
        // swallow
      }
    };

    void fetchOnce();
    pollTimerRef.current = window.setInterval(fetchOnce, POLL_INTERVAL_MS);
    return () => {
      if (pollTimerRef.current !== null) {
        window.clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [tf.teamId, tf.runId, wizard.organizationSlug]);

  const onChip = (value: string) => {
    wizard.patchTeamFlow({ teamName: value, teamNameError: null });
  };

  const onSubmit = async () => {
    if (!tf.teamId || !tf.runId || !wizard.organizationSlug) return;
    const name = tf.teamName.trim();
    if (!name) return;
    setSubmitting(true);
    wizard.patchTeamFlow({ teamNameError: null });
    try {
      const result = await patchTeamName({
        orgSlug: wizard.organizationSlug,
        runId: tf.runId,
        teamId: tf.teamId,
        name,
      });
      wizard.patchTeamFlow({ teamNameFromServer: result.name });
      if (tf.runId && tf.teamId) {
        try {
          sessionStorage.setItem(`clavis_team_for_run_${tf.runId}`, tf.teamId);
        } catch {
          // sessionStorage disabled
        }
      }
      // `tf.runId` is guaranteed truthy here (guarded at the top of onSubmit),
      // so the happy path always lands on the participant run view. The former
      // `/join/:code/team/ready` fallback was unreachable and its route has
      // been removed (#130).
      navigate(`/runs/${tf.runId}/participant`);
    } catch (err) {
      let mapped: 'forbidden' | 'unknown' = 'unknown';
      let serverDetail: string | null = null;
      if (err instanceof ApiError) {
        serverDetail = err.detail || null;
        if (err.status === 403 || err.status === 409 || err.status === 422) {
          mapped = 'forbidden';
        }
        console.warn('[PickTeamNameStep] PATCH failed', {
          status: err.status,
          detail: err.detail,
          name,
          teamId: tf.teamId,
          orgSlug: wizard.organizationSlug,
        });
      }
      wizard.patchTeamFlow({ teamNameError: mapped, teamNameErrorDetail: serverDetail });
    } finally {
      setSubmitting(false);
    }
  };

  // Show only real members from the server. Falling back to wizard.displayName
  // would surface a stale per-user name (e.g. a previous run) on every team —
  // so during the polling-empty window we render a neutral placeholder.
  const memberList =
    members.length === 0 ? '…' : members.map((m) => m.display_name).join(', ');

  const hasError = tf.teamNameError !== null;
  const submitDisabled = !tf.teamName.trim() || submitting;

  return (
    <OnboardingShellV2
      progress={
        <ProgressIndicatorV2
          current={step.current}
          total={step.total}
        />
      }
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          paddingBlockStart: '66px',
          gap: '24px',
        }}
      >
        <Box
          component="img"
          src={heroFerris}
          alt=""
          sx={{
            width: '93px',
            height: '86px',
            alignSelf: 'center',
            display: 'block',
            objectFit: 'contain',
          }}
        />
        <Typography
          component="h1"
          sx={{
            fontFamily: "'Roboto', sans-serif",
            fontSize: '24px',
            fontWeight: 600,
            lineHeight: '32px',
            letterSpacing: 0,
            color: '#322F35',
            margin: 0,
          }}
        >
          {t('pickName.title')}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Typography
            sx={{
              fontFamily: "'Noto Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '24px',
              letterSpacing: '0.15px',
              color: '#322F35',
              // Half-leading compensation (figma-implement skill §6 sub-fix):
              // body/large CSS line-box has 3 px half-leading above the glyph,
              // Figma's text-bbox has 0 → pull the element up so the glyph
              // lands at Figma's y-coordinate.
              marginBlockStart: '-3px',
            }}
          >
            {t('pickName.membersHeading')}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Noto Sans', sans-serif",
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              letterSpacing: '0.25px',
              color: '#322F35',
              // iPhone-12-Audit: heading carries -3 half-leading but its CSS
              // box is +1 px taller than Figma bbox; without comp the value
              // glyph lands +3 below Figma-Y. Pull up to match.
              marginBlockStart: '-3px',
              // Figma value-bbox h=23 vs CSS lh=20 → block bottom is 3 px
              // shorter than Figma. Pad bottom so info-box sibling lands at
              // Figma-Y=305 (otherwise info-box drifts up by 3).
              paddingBlockEnd: '3px',
            }}
          >
            {memberList}
          </Typography>
        </Box>
        <Box
          sx={{
            backgroundColor: '#E6E9F4',
            borderRadius: '22px',
            // Figma Message bbox = 107 px (node.json), CSS-content sums to ~95
            // (13 + 18 chip + 12 gap + ~40 body 2-line + 13). The 12 px delta
            // is Figma auto-layout slack — render it as asymmetric bottom
            // padding so the visible box matches Figma's 107 px height.
            paddingBlockStart: '13px',
            paddingBlockEnd: '25px',
            paddingInline: '14px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          <Box
            sx={{
              alignSelf: 'flex-start',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#4D69B9',
              color: '#FFFFFF',
              borderRadius: '8px',
              paddingInline: '8px',
              paddingBlock: '1px',
              fontFamily: "'Noto Sans', sans-serif",
              fontSize: '11px',
              fontWeight: 500,
              lineHeight: '16px',
              letterSpacing: '0.5px',
            }}
          >
            <Box
              component="img"
              src={infoIcon}
              alt=""
              aria-hidden
              sx={{
                width: '12px',
                height: '12px',
                display: 'block',
                // Vendored info.svg paths are #1D1B20 — flip to white to match
                // Figma's chip-icon fill #FFFFFF without shipping a duplicate.
                filter: 'brightness(0) invert(1)',
              }}
            />
            Info
          </Box>
          <Typography
            sx={{
              fontFamily: "'Noto Sans', sans-serif",
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '20px',
              letterSpacing: '0.25px',
              color: '#00348D',
            }}
          >
            {t('pickName.info')}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontFamily: "'Noto Sans', sans-serif",
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '24px',
            letterSpacing: '0.5px',
            color: '#322F35',
            // Visible-gap calibration (figma-implement skill §6):
            // iPhone-12-Audit (iter 2): combined with members-value -3 the
            // upstream block is 3 px shorter, so -6 still left discuss at
            // -5. -3 lands discuss at Figma-Y; subsequent cascade balanced
            // via TF + action-row comps.
            marginBlockStart: '-3px',
          }}
        >
          {t('pickName.discussionPrompt')}
        </Typography>
        <ClavisTextField
          label={t('pickName.nameLabel')}
          helperText={
            hasError
              ? `${t(`pickName.errors.${tf.teamNameError}`)}${
                  tf.teamNameErrorDetail ? ` (${tf.teamNameErrorDetail})` : ''
                }`
              : t('pickName.nameHelper')
          }
          error={hasError}
          value={tf.teamName}
          onChange={(e) =>
            wizard.patchTeamFlow({
              teamName: e.target.value,
              teamNameError: null,
            })
          }
          onKeyDown={(e) => {
            if (e.key === 'Enter') void onSubmit();
          }}
          slotProps={{ htmlInput: { maxLength: 100 } }}
          fullWidth
          variant="outlined"
          sx={{
            // iPhone-12-Audit: residual +3 drift at TF after discuss-comp +
            // members-block pad-end. Pull TF up by 6 → glyph at Figma-Y (524).
            marginBlockStart: '-6px',
            // Figma supporting-text sits flush with the field's bottom-stroke
            // (y=580 = TF bbox bottom). MUI's default `FormHelperText` has
            // marginTop=3, which pushes the helper 3 px lower. Override to 0
            // so the helper-bbox top matches Figma exactly.
            '& .MuiFormHelperText-root': { marginTop: 0 },
            // Figma error-frame: stroke + label + supporting-text all #8C1D18.
            // MUI's default error.main is #d32f2f; override locally.
            '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline':
              { borderColor: '#8C1D18' },
            '& .MuiOutlinedInput-root.Mui-error:hover .MuiOutlinedInput-notchedOutline':
              { borderColor: '#8C1D18' },
            '& .MuiInputLabel-root.Mui-error': { color: '#8C1D18' },
            '& .MuiFormHelperText-root.Mui-error': { color: '#8C1D18' },
          }}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            // Visible-gap calibration (figma-implement skill §6):
            // iPhone-12-Audit: helper-marginTop:0 collapsed MUI box by 3,
            // so sugg starts at +3 above prior position. Bump comp 4→7 to
            // keep sugg-title at Figma-Y=628.
            marginBlockStart: '7px',
          }}
        >
          <Typography
            sx={{
              fontFamily: "'Noto Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '24px',
              letterSpacing: '0.5px',
              color: '#322F35',
            }}
          >
            {t('pickName.suggestionsTitle')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {suggestions.map((s) => (
              <Box
                key={s}
                component="button"
                type="button"
                onClick={() => onChip(s)}
                sx={{
                  all: 'unset',
                  boxSizing: 'border-box',
                  cursor: 'pointer',
                  height: '32px',
                  paddingInline: '12px',
                  borderRadius: '8px',
                  // Figma: stroke=#CAC5CD@1.0 align=INSIDE — inset shadow
                  // matches Figma's INSIDE-stroke semantics without inflating
                  // the box (CSS border would add 2 px width vs Figma's bbox).
                  border: 'none',
                  boxShadow: 'inset 0 0 0 1px #CAC5CD',
                  fontFamily: "'Roboto', sans-serif",
                  fontSize: '14px',
                  fontWeight: 500,
                  lineHeight: '20px',
                  letterSpacing: '0.1px',
                  color: '#322F35',
                  display: 'inline-flex',
                  alignItems: 'center',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                  ...pillButtonFocusRing,
                }}
              >
                {s}
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: '16px', marginBlockStart: '6px' }}>
          <Box
            component="button"
            type="button"
            onClick={() => navigate(-1)}
            sx={{
              all: 'unset',
              boxSizing: 'border-box',
              cursor: 'pointer',
              minHeight: '40px',
              paddingInline: '16px',
              borderRadius: '100px',
              border: 'none',
              boxShadow: 'inset 0 0 0 1px #CAC4D0',
              color: '#49454F',
              fontFamily: "'Roboto', sans-serif",
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '20px',
              letterSpacing: '0.1px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': { backgroundColor: 'rgba(73, 69, 79, 0.04)' },
              ...pillButtonFocusRing,
            }}
          >
            {t('pickName.back')}
          </Box>
          <Box
            component="button"
            type="button"
            onClick={onSubmit}
            disabled={submitDisabled}
            sx={{
              all: 'unset',
              boxSizing: 'border-box',
              cursor: submitDisabled ? 'not-allowed' : 'pointer',
              minHeight: '40px',
              paddingInline: '16px',
              borderRadius: '100px',
              backgroundColor: submitDisabled
                ? 'rgba(29, 27, 32, 0.12)'
                : '#4D69B9',
              color: submitDisabled ? '#605D64' : '#FFFFFF',
              fontFamily: "'Roboto', sans-serif",
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '20px',
              letterSpacing: '0.1px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                backgroundColor: submitDisabled
                  ? 'rgba(29, 27, 32, 0.12)'
                  : '#3D54A0',
              },
              ...pillButtonFocusRing,
            }}
          >
            {t('pickName.submit')}
          </Box>
        </Box>
      </Box>
    </OnboardingShellV2>
  );
};

export default PickTeamNameStep;
